import { Suspense, lazy } from 'react';
import { Profile } from '@alliance-disposal/transport-types';
import { Auth0LoginRedirect } from '@wayste/auth-ui-shell';
import { Navigate, createBrowserRouter, useLocation } from 'react-router-dom';
import App from '../App';
import PageLoader from '../components/PageLoader/PageLoader';
import PostHogPageView from '../components/pageview';
import { S3Provider } from '../context';
import { DispatchTaskTypes } from '../pages/dispatch/utils/dispatch-params';
import routes from '../utils/routes';
import ErrorBoundary from './error-boundary';
import AppAdminWrapper from './route-protections/app-admin-wrapper';
import AuthWrapper from './route-protections/auth-wrapper';
import FeatureFlagRouteSwitch from './route-protections/feature-flag-route-switch';
import FeatureFlagWrapper from './route-protections/feature-flag-wrapper';
import PublicOnlyWrapper from './route-protections/public-only-wrapper';
import RestrictedAccessWrapper from './route-protections/restricted-access-wrapper';
import UnifiedDispatchGuard from './route-protections/unified-dispatch-guard';
import UnifiedDispatchRedirect from './route-protections/unified-dispatch-redirect';
import WaysteStandardWrapper from './route-protections/wayste-standard-wrapper';

// Legacy roll-off pages and their operations-roll-off replacements. When the
// `wayste-operations-rolloff` flag is enabled, the legacy paths render the new UI.
const OrderList = lazy(() => import('../pages/OrderList'));
const OperationsRollOffOrders = lazy(() => import('../pages/operations-roll-off/operations-roll-off-orders'));
const OrderCreate = lazy(() => import('../pages/order-create'));
const RollOffOrderCreate = lazy(() => import('../pages/operations-roll-off/roll-off-order-create'));
const RoSchedule = lazy(() => import('../pages/ro-scheduling/ro-schedule'));
const OperationsRollOffSchedules = lazy(() => import('../pages/operations-roll-off/operations-roll-off-schedules'));

export type RouterOutletContext = {
    userProfile: Profile.ProfileTransport;
    waysteLite: boolean;
    isAppAdmin: boolean;
    /** Present only for app admins, who may skip onboarding to reach the hauler switcher. */
    onExitSetup?: () => void;
};

// AuthWrapper redirects unauthenticated users in place, so nothing in the app links here any more. The route is
// kept only so /login stays resolvable for old emails and bookmarks; PublicOnlyWrapper bounces an
// already-authenticated visitor before it renders.
const LoginRedirect = () => {
    const location = useLocation();

    return <Auth0LoginRedirect returnTo={location.pathname + location.search} loader={<PageLoader />} />;
};

// The confirmation link haulers receive by email moved from /update-alliance/ to /update-sourgum/.
// Messages sent before the rename are already in inboxes and still carry the old path, so it stays
// resolvable rather than 404ing. The query string carries the whole payload (category, id, switch)
// and must survive the hop.
//
// SAFE TO DELETE AFTER 2026-10-31 — by then every email predating the rename is long past the
// confirmation window. Remove this component and its route entry together.
const LegacyUpdateAllianceRedirect = () => {
    const location = useLocation();

    return <Navigate to={`${routes.public.updateAdmin}${location.search}`} replace />;
};

export const router = createBrowserRouter([
    {
        path: '/',
        element: (
            <Suspense fallback={<PageLoader />}>
                <S3Provider>
                    <App />
                    {/* Tracks pageviews manually because it needs the context from react-router */}
                    <PostHogPageView />
                </S3Provider>
            </Suspense>
        ),
        errorElement: <ErrorBoundary />,
        children: [
            {
                element: <AuthWrapper />,
                children: [
                    {
                        path: routes.dashboard,
                        lazy: () => import('../pages/dashboard/dashboard').then((module) => ({ Component: module.default })),
                    },
                    {
                        path: routes.orders.list,
                        element: (
                            <FeatureFlagRouteSwitch
                                requiredFeatureFlag="wayste-operations-rolloff"
                                EnabledComponent={OperationsRollOffOrders}
                                FallbackComponent={OrderList}
                            />
                        ),
                    },
                    {
                        element: <AppAdminWrapper />,
                        children: [
                            {
                                path: routes.appAdmin.list,
                                lazy: () => import('../pages/app-admin/app-admin').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: routes.appAdmin.audit,
                                lazy: () =>
                                    import('../pages/app-admin/audit-logs/audit-logs-list').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.appAdmin.customerImport,
                                lazy: () =>
                                    import('../pages/app-admin/customer-import/customer-import').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.lab.list,
                                lazy: () => import('../components/XXXLab').then((module) => ({ Component: module.default })),
                            },
                        ],
                    },
                    {
                        element: <RestrictedAccessWrapper blockBilling />,
                        children: [
                            {
                                // Gated rather than wrapped in WaysteStandardWrapper — see UnifiedDispatchGuard for why.
                                element: <UnifiedDispatchGuard />,
                                children: [
                                    {
                                        path: routes.dispatch.list,
                                        lazy: () => import('../pages/dispatch/dispatch').then((module) => ({ Component: module.default })),
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        element: <WaysteStandardWrapper />,
                        children: [
                            {
                                element: <RestrictedAccessWrapper blockBilling />,
                                children: [
                                    {
                                        path: routes.orders.create,
                                        element: (
                                            <FeatureFlagRouteSwitch
                                                requiredFeatureFlag="wayste-operations-rolloff"
                                                EnabledComponent={RollOffOrderCreate}
                                                FallbackComponent={OrderCreate}
                                            />
                                        ),
                                    },
                                    {
                                        element: <UnifiedDispatchRedirect taskType={DispatchTaskTypes.ROLL_OFF} />,
                                        children: [
                                            {
                                                path: routes.roScheduling.list,
                                                element: (
                                                    <FeatureFlagRouteSwitch
                                                        requiredFeatureFlag="wayste-operations-rolloff"
                                                        EnabledComponent={OperationsRollOffSchedules}
                                                        FallbackComponent={RoSchedule}
                                                    />
                                                ),
                                            },
                                        ],
                                    },
                                    {
                                        path: routes.driverTracking.list,
                                        lazy: () =>
                                            import('../pages/driver-tracking/driver-tracking').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.equipment.vehicles,
                                        lazy: () =>
                                            import('../pages/equipment/vehicle-page').then((module) => ({ Component: module.default })),
                                    },
                                    {
                                        path: routes.equipment.containers,
                                        lazy: () =>
                                            import('../pages/equipment/container-page').then((module) => ({ Component: module.default })),
                                    },
                                    {
                                        path: routes.equipment.inventory,
                                        lazy: () =>
                                            import('../pages/equipment/inventory-page').then((module) => ({ Component: module.default })),
                                    },
                                    {
                                        path: routes.equipment.map,
                                        lazy: () =>
                                            import('../pages/equipment/container-map').then((module) => ({ Component: module.default })),
                                    },
                                    {
                                        path: routes.dispatching.dashboard,
                                        lazy: () =>
                                            import('../pages/commercial-dispatching/dispatcher-dashboard').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.dispatching.commercialRoutes.list,
                                        lazy: () =>
                                            import('../pages/commercial-dispatching/route-list').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.dispatching.commercialRoutes.details(':id'),
                                        lazy: () =>
                                            import('../pages/commercial-dispatching/route-details').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.dispatching.routing(),
                                        lazy: () =>
                                            import('../pages/commercial-dispatching/routing-view-wrapper').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.dispatching.reschedule,
                                        lazy: () =>
                                            import('../pages/commercial-dispatching/route-reschedule').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.dispatching.schedule,
                                        lazy: () =>
                                            import('../pages/commercial-dispatching/route-schedule').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.dispatching.events,
                                        lazy: () =>
                                            import('../pages/commercial-dispatching/event-list').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.dispatching.containerDelivery,
                                        lazy: () =>
                                            import('../pages/commercial-dispatching/delivery-pickup-schedule/schedule').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                ],
                            },
                            {
                                element: <RestrictedAccessWrapper blockBilling blockDispatch />,
                                children: [
                                    {
                                        path: routes.reports.list,
                                        lazy: () =>
                                            import('../pages/reports/report-list').then((module) => ({ Component: module.default })),
                                    },
                                    {
                                        path: routes.reports.taxLiability,
                                        lazy: () =>
                                            import('../pages/reports/sales-tax-liability-report').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.reports.paymentsBreakdown,
                                        lazy: () =>
                                            import('../pages/reports/payments-breakdown-report').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.reports.rollOffSalesRevenue,
                                        lazy: () =>
                                            import('../pages/reports/roll-off-sales-revenue').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.reports.rollOffDeliveries,
                                        lazy: () =>
                                            import('../pages/reports/roll-off-deliveries').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.reports.rollOffBilling,
                                        lazy: () =>
                                            import('../pages/reports/roll-off-billing').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.reports.rollOffOperations,
                                        lazy: () =>
                                            import('../pages/reports/roll-off-operations').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                    {
                                        path: routes.reports.rollOffOnlineOrders,
                                        lazy: () =>
                                            import('../pages/reports/roll-off-online-orders').then((module) => ({
                                                Component: module.default,
                                            })),
                                    },
                                ],
                            },
                            {
                                path: routes.orders.update(':id'),
                                lazy: () => import('../components/OrderUpdate').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: routes.timeSheet.list,
                                lazy: () =>
                                    import('../pages/time-sheet/time-sheet').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.subscriptions.list,
                                lazy: () =>
                                    import('../pages/commercial-crm/subscription-list').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.subscriptions.create,
                                lazy: () =>
                                    import('../pages/commercial-crm/subscription-forms/subscription-create').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.subscriptions.editEquipment(':id'),
                                lazy: () =>
                                    import('../pages/commercial-crm/subscription-forms/subscription-equipment-update').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.subscriptions.details(':id'),
                                lazy: () =>
                                    import('../pages/commercial-crm/subscription-details').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.taskBoard,
                                lazy: () => import('../pages/task-board/task-board-page').then((module) => ({ Component: module.default })),
                            },
                        ],
                    },
                    {
                        path: routes.orders.details(':id'),
                        lazy: () => import('../components/OrderDetails').then((module) => ({ Component: module.default })),
                    },
                    {
                        path: routes.customers.details(':id'),
                        lazy: () => import('../components/CustomerDetails').then((module) => ({ Component: module.default })),
                    },
                    {
                        path: routes.customers.list,
                        lazy: () => import('../pages/customer-list/customer-list').then((module) => ({ Component: module.default })),
                    },
                    {
                        element: <RestrictedAccessWrapper blockDispatch />,
                        children: [
                            {
                                path: routes.billing.list,
                                lazy: () => import('../pages/billing/billing-page').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: routes.billing.combineInvoices,
                                lazy: () => import('../pages/billing/combine-invoices').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: routes.billingV2.invoices,
                                lazy: () => import('../pages/billing-v2/invoices-page').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: routes.billingV2.invoiceDetails(':invoiceId'),
                                lazy: () =>
                                    import('../pages/billing-v2/invoice-details-page').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: routes.partnerPortalConnect.payouts,
                                lazy: () =>
                                    import('../pages/partner-portal-connect/partner-portal-payouts-page').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.settings.accountingIntegrations,
                                lazy: () =>
                                    import('../pages/account-settings/pages/accounting-integrations').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.settings.quickBooksOnlineMapping,
                                lazy: () =>
                                    import('../pages/account-settings/pages/quickbooks-online-settings').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.settings.quickBooksOnlineAuthCallback,
                                lazy: () =>
                                    import('../pages/account-settings/pages/QuickBooksOnlineAuthCallback').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.settings.quickBooksDesktopSettings,
                                lazy: () =>
                                    import('../pages/account-settings/pages/quickbooks-desktop-settings').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.settings.quickBooksDesktopDownload,
                                lazy: () =>
                                    import('../pages/account-settings/pages/quickbooks-desktop-download').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.settings.billing.onboard,
                                lazy: () =>
                                    import('../pages/account-settings/pages/billing-onboard').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.settings.billing.dashboard,
                                lazy: () =>
                                    import('../pages/account-settings/pages/billing-payment-dashboard').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.liftline.list,
                                lazy: () => import('../pages/liftline/liftline').then((module) => ({ Component: module.default })),
                            },
                        ],
                    },
                    {
                        element: <RestrictedAccessWrapper blockBilling blockDispatch />,
                        children: [
                            {
                                path: routes.pricing.list,
                                lazy: () =>
                                    import('../pages/HaulerPricing/hauler-pricing').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: routes.pricing.details(':id'),
                                lazy: () =>
                                    import('../pages/HaulerPricingDetails/hauler-pricing-details').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.users.update(':id'),
                                lazy: () =>
                                    import('../pages/account-settings/pages/user-update').then((module) => ({ Component: module.default })),
                            },
                        ],
                    },
                    {
                        element: <FeatureFlagWrapper requiredFeatureFlag="wayste-portable-toilet" />,
                        children: [
                            {
                                path: routes.portableToilets.list,
                                lazy: () =>
                                    import('../pages/portable-toilets/portable-toilets').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.portableToilets.create,
                                lazy: () =>
                                    import('../pages/portable-toilets/portable-toilet-create').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.portableToilets.update(':subscriptionId'),
                                lazy: () =>
                                    import('../pages/portable-toilets/portable-toilet-edit-services').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                path: routes.portableToilets.details(':subscriptionId'),
                                lazy: () =>
                                    import('../pages/portable-toilets/portable-toilet-details').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                            {
                                element: <RestrictedAccessWrapper blockBilling />,
                                children: [
                                    {
                                        element: <UnifiedDispatchRedirect taskType={DispatchTaskTypes.PORTABLE_TOILET} />,
                                        children: [
                                            {
                                                path: routes.portableToilets.ptDispatch,
                                                lazy: () =>
                                                    import('../pages/portable-toilets/portable-toilet-dispatch-schedule').then(
                                                        (module) => ({
                                                            Component: module.default,
                                                        }),
                                                    ),
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        element: <FeatureFlagWrapper requiredFeatureFlag="wayste-operations-rolloff" />,
                        children: [
                            {
                                path: routes.operationsRollOff.details(':subscriptionId', ':ordId', ':ordRoId'),
                                lazy: () =>
                                    import('../pages/operations-roll-off/order-details/order-details').then((module) => ({
                                        Component: module.default,
                                    })),
                            },
                        ],
                    },
                    {
                        path: routes.requestForProposal.rfpsList,
                        lazy: () =>
                            import('../pages/sourgum-request-for-proposal/rfp-list-view').then((module) => ({
                                Component: module.default,
                            })),
                    },
                    {
                        path: routes.partnerPortalConnect.list,
                        lazy: () =>
                            import('../pages/partner-portal-connect/partner-portal-connect').then((module) => ({
                                Component: module.default,
                            })),
                    },
                    {
                        path: routes.partnerPortalConnect.plaidOAuth,
                        lazy: () =>
                            import('../pages/partner-portal-connect/plaid-oauth-callback').then((module) => ({
                                Component: module.default,
                            })),
                    },
                    {
                        path: routes.settings.list,
                        lazy: () => import('../pages/account-settings').then((module) => ({ Component: module.default })),
                    },
                    {
                        path: routes.search.list,
                        lazy: () => import('../pages/GlobalSearch').then((module) => ({ Component: module.default })),
                    },
                    {
                        path: routes.commercialPreview,
                        lazy: () => import('../pages/commercial-preview').then((module) => ({ Component: module.default })),
                    },
                    ...(process.env.NODE_ENV === 'development'
                        ? [
                              {
                                  path: routes.devTest,
                                  lazy: () => import('../pages/dev-test').then((module) => ({ Component: module.default })),
                              },
                          ]
                        : []),
                    {
                        path: '*',
                        lazy: () => import('../structure/not-found').then((module) => ({ Component: module.default })),
                    },
                ],
            },
            {
                element: <PublicOnlyWrapper />,
                children: [
                    {
                        path: routes.auth.login,
                        element: <LoginRedirect />,
                    },
                ],
            },
            // Profile setup requires a session but renders without the app chrome, so App decides
            // whether to show it rather than Layout wrapping it (see the profile-setup gate there).
            {
                element: <AuthWrapper />,
                children: [
                    {
                        path: routes.public.welcome,
                        lazy: () => import('../pages/welcome/welcome-page').then((module) => ({ Component: module.default })),
                    },
                ],
            },
            // Access for both public and authenticated users
            {
                path: routes.public.updateAdmin,
                lazy: () => import('../pages/public/HaulerToAdminUpdates').then((module) => ({ Component: module.default })),
            },
            // SAFE TO DELETE AFTER 2026-10-31 — see LegacyUpdateAllianceRedirect above.
            {
                path: '/update-alliance/',
                element: <LegacyUpdateAllianceRedirect />,
            },
        ],
    },
]);
